import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useLoading } from '../../../hooks/useLoading';
import { ListItemIcon, Menu, MenuItem } from '@mui/material';
import { getNavigationUrl } from '../../../common/helpers/navigationHelpers';
import { Link } from 'react-router-dom';
import { Edit, HowToReg, PersonRemove } from '@mui/icons-material';
import MaterailTable from '../Elements/MaterialTable';
import { FaEllipsisV } from 'react-icons/fa';
import { ProgressLoader } from '../Elements';
import ConfirmationModal from '../ConfirmationModal';
import { getStatusInfo } from '../TableStatusView';
import { CustomerListType } from '../../../common/types/CustomerTypes';
import { tableEmptyMessages } from '../../../constants/messages/tableEmptyMessages';
import { ApiResponseType } from '../../../common/api/apiHelper';
import commonNetwork from '../../../common/api/commonNetwork';
import { toast } from 'react-toastify';
import { successMessages } from '../../../constants/messages/successMessages';
import { ThunkDispatch } from 'redux-thunk';
import { refreshCustomerTable } from '../../../common/helpers/refreshTables/refreshCustomerTable';
import { muiMenuStyles } from '../../../constants/muiMenuStyles';
import { getWebCustomerList } from '../../../store/slices/webCustomersSlice';

interface CustomerTableProps {
  formFilterRef: React.Ref<HTMLDivElement> | undefined;
  getHeadCells: (values: any) => void;
  tableSelectedRows: any;

  // Pagination için
  page: number;
  pageSize: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  requestParameters: any;
  setSelectAll: React.Dispatch<React.SetStateAction<boolean>>;
}

const CustomerTable: React.FC<CustomerTableProps> = ({
  getHeadCells,
  tableSelectedRows,
  page,
  pageSize,
  setPage,
  setPageSize,
  requestParameters,
  setSelectAll }) => {
  const classes = muiMenuStyles();

  const thunkdispatch = useDispatch<ThunkDispatch<any, any, any>>();

  //useRef's
  const confirmModalRef = useRef<any>();

  // useState's
  const [anchorEl, setAnchorEl] = useState(null);
  const [dropdownMenuId, setDropdownMenuId] = useState("");
  const [dropdownAllValues, setDropdownAllValues] = useState<CustomerListType>();
  const [operationsLoading, setOperationsLoading] = useState(false);

  useEffect(() => {
    getHeadCells(headCells);
    refreshCustomerTable(thunkdispatch);
  }, []);

  const isopenMenu = Boolean(anchorEl);

  const {
    customerList,
    totalCount,
    currentPage,
    isFetching,
    isloadingIntegration,
    isFetchComplete
  } = useSelector((state: any) => state.webCustomers);

  const loading = useLoading(isFetching);

  const handleClickMenu = (event: any, row: any) => {
    setDropdownAllValues(row);
    setDropdownMenuId(row.customerId);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatus = async (customerId: any, status: any) => {
    const paramaters = {
      customerId: customerId,
      status: status ? false : true
    }

    let result: ApiResponseType;

    result = await commonNetwork.post("/customer/update-status", paramaters);

    if (result.isSuccess) {
      toast.success(status ? successMessages.statusPassive : successMessages.statusActive);
      refreshCustomerTable(thunkdispatch);
      setAnchorEl(null);
    } else {
      toast.error(result.errorMessage);
    }
  }

  const LinearColor = () => {
    if (operationsLoading || isloadingIntegration) {
      return <ProgressLoader />;
    }
    return null;
  };

  const DropdownMenu = (row: any) => {
    const status = dropdownAllValues?.status;
    return (
      <Menu
        id="customer-operations"
        anchorEl={anchorEl}
        open={isopenMenu}
        onClose={handleClose}
        classes={{
          paper: classes.paper,
        }}
      >
        <MenuItem component={Link} to={getNavigationUrl(`/musteri-guncelle/` + dropdownAllValues?.customerId)}
          onClick={() => {
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" color="primary" />
          </ListItemIcon>
          Düzenle
        </MenuItem>
        <MenuItem onClick={() => handleStatus(dropdownAllValues?.customerId, dropdownAllValues?.status)}>
          <ListItemIcon>
            {dropdownAllValues?.status ? (
              <PersonRemove color="error" />
            ) : (
              <HowToReg color="success" />
            )}
          </ListItemIcon>
          {dropdownAllValues?.status ? "Müşteriyi Pasif Et" : "Müşteriyi Aktif Et"}
        </MenuItem>
      </Menu>
    );
  }

  const headCells = [
    {
      id: 'operations',
      numeric: false,
      label: 'İşlemler',
      exceptToExcel: true,
      isRender: true,
      disableSortIcon: true,
      renderRow: (row: any) => {

        return (
          <div className="flex flex-row justify-around items-center">
            <a id="report-operations-a"
              aria-controls={isopenMenu ? 'report-operations' : undefined}
              aria-haspopup="true"
              aria-expanded={isopenMenu ? 'true' : undefined}
              className="cursor-pointer"
              onClick={(event) => handleClickMenu(event, row)}
            >
              <FaEllipsisV
                className="block sm:block h-3 w-3 font-light	ml-1"
                aria-hidden="true"
              />
            </a>
            <DropdownMenu />
          </div>
        );

      }
    },
    {
      id: 'customerId',
      numeric: false,
      label: 'ID',
      isprimary: true,
      className: "min-w-[80px]",
      colWidth: 10, // For excel
      hide: true,
      exceptToExcel: true
    },
    {
      id: 'customerCode',
      numeric: true,
      label: 'Cari Hesap Kodu',
      className: "min-w-[250px]",
      maxLength: 30,
      colWidth: 20, // For excel
    },
    {
      id: 'customerName',
      numeric: true,
      label: 'Cari Hesap Ünvan',
      className: "min-w-[360px]",
      maxLength: 42,
      colWidth: 20, // For excel
    },
    {
      id: 'taxCode',
      numeric: true,
      label: 'VKN/TCKN',
      colWidth: 20, // For excel
      className: "min-w-[110px]",
      maxLength: 15,
      isRender: true,
      renderRow: (row: any) => (
        <>
          {row.taxCode || row.tcCode}
        </>
      )
    },
    {
      id: 'city',
      numeric: true,
      label: 'Şehir',
      className: "min-w-[120px]",
      maxLength: 15,
      colWidth: 20, // For excel
    },
    {
      id: 'district',
      numeric: true,
      label: 'İlçe',
      className: "min-w-[120px]",
      maxLength: 15,
      colWidth: 20, // For excel
    },
    {
      id: 'phone',
      numeric: true,
      label: 'Telefon',
      colWidth: 20, // For excel
      className: "min-w-[110px]",
      maxLength: 15,
    },
    {
      id: 'email',
      numeric: true,
      label: 'E-Posta Adresi',
      className: "max-w-[220px]",
      maxLength: 30,
      colWidth: 30, // For excel
    },
    {
      id: 'status',
      numeric: true,
      disablePadding: false,
      className: "min-w-[130px]",
      isRender: true,
      label: 'Durum',
      colWidth: 20,
      renderRow: (row: any) => (
        <>
          {getStatusInfo(row.status)}
        </>
      )
    },
    {
    id: 'customerUserList',
    numeric: false,
    disablePadding: false,
    className: "min-w-[130px]",
    label: 'Bağlı Kullanıcılar',
    colWidth: 30,
    hide: true, 
    exceptToExcel: false,
    }
  ];


  return (
    <>
      {LinearColor()}
      <ConfirmationModal ref={confirmModalRef} />
      <MaterailTable
        ref={tableSelectedRows}
        headerCells={headCells}
        tableData={customerList}
        isRowSelect={true}
        isLoading={loading}
        emptyMessage={isFetchComplete ? tableEmptyMessages.isFilteredDataNotFound : tableEmptyMessages.emptyString}
        onPageChange={(newPage) => {
          setPage(newPage);
          thunkdispatch(getWebCustomerList({
            ...requestParameters,
            pageNumber: newPage,
            pageSize: pageSize
          }));
        }}
        onRowsPerPageChange={(newRows) => {
          setPageSize(newRows);
          setPage(1);
          thunkdispatch(getWebCustomerList({
            ...requestParameters,
            pageNumber: 1,
            pageSize: newRows
          }));
        }}
        totalDataCount={totalCount}
        currentPage={currentPage}
        setSelectAll={setSelectAll}
      />
    </>
  )
}

export default CustomerTable;